import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  MdSync,
  MdPersonSearch,
  MdCheck,
  MdEdit,
  MdAdd,
  MdArrowBack,
  MdChevronLeft,
  MdChevronRight,
} from "react-icons/md";
import toast from "react-hot-toast";
import SearchBar from "../SearchBar/SearchBar";
import ZoomButton from "../buttons/ZoomButton";
import EditButton from "../buttons/EditButton";
import UserImageUploadModal from "../UserImageUploadModal/UserImageUploadModal";
import InternalPagination from "../InternalPagination/InternalPagination";
import Toggle from "../Toggle/Toggle";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useModelUpload } from "../../customHook/ModelSelector/useModelUpload";

const ExpandableTitle = ({ title, className = "" }) => {
  const [showFullTitle, setShowFullTitle] = useState(false);

  return (
    <p
      className={`text-[9px] font-bold text-primary cursor-pointer leading-[14px] transition-all duration-200 min-w-0 flex-1 ${className}`}
      style={{
        display: showFullTitle ? "block" : "-webkit-box",
        WebkitLineClamp: showFullTitle ? "none" : 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        maxHeight: showFullTitle ? "none" : "28px",
      }}
      onClick={(e) => {
        e.stopPropagation();
        setShowFullTitle((prev) => !prev);
      }}
      title={title}
    >
      {title}
    </p>
  );
};

const ModelCard = ({
  data,
  selectionMode,
  isActive,
  userRole,
  onSelect,
  openModal,
  handleToggleModel,
  onEditModel,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const getImages = (item) => {
    if (Array.isArray(item?.images) && item.images.length > 0) {
      return item.images.filter(Boolean);
    }
    const list = [item?.front || item?.image, item?.back].filter(Boolean);
    return list;
  };

  const images = getImages(data);
  const currentImg = images[currentImageIndex] || images[0] || "";
  const hasMultipleImages = images.length > 1;

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onSelect();
        }
      }}
      className={`model-card app-card app-card-hover w-full relative text-left overflow-hidden group focus:outline-none flex flex-col justify-between cursor-pointer ${
        isActive
          ? "border-primary border-2 shadow-[0_0_0_3px_rgba(26,26,26,0.06),0_8px_16px_-4px_rgba(26,26,26,0.04)]"
          : "border-outline hover:border-slate-300"
      }`}
    >
      {/* Selected Checkmark Overlay */}
      {isActive && (
        <span className="absolute top-1.5 left-1.5 z-10 w-4 h-4 bg-primary text-on-primary rounded-full flex items-center justify-center shadow-sm animate-fade-in">
          <MdCheck size={10} />
        </span>
      )}

      {/* Edit Button - Visible in Admin mode on top right */}
      {userRole === "admin" && onEditModel && (
        <EditButton
          onClick={(e) => {
            e.stopPropagation();
            onEditModel(data);
          }}
          positionClasses="absolute top-1.5 right-1.5 z-10"
          size="md"
        />
      )}

      {/* Card Image */}
      <div
        className={`w-full aspect-[5/6] overflow-hidden relative group/img shrink-0 ${
          selectionMode === "user" ? "bg-slate-100" : "bg-slate-100"
        }`}
      >
        {currentImg ? (
          <img
            className={`w-full h-full object-center transition-transform duration-500 hover:scale-105 ${
              selectionMode === "user" ? "object-contain" : "object-cover"
            }`}
            src={currentImg}
            alt={data.name || data.title}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">
            No Image
          </div>
        )}

        {/* Left Arrow Button */}
        {hasMultipleImages && (
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
            aria-label="Previous image"
          >
            <MdChevronLeft size={16} />
          </button>
        )}

        {/* Right Arrow Button */}
        {hasMultipleImages && (
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-5.5 h-5.5 rounded-full bg-white/90 backdrop-blur-sm text-slate-800 flex items-center justify-center hover:bg-white shadow-md transition-all cursor-pointer"
            aria-label="Next image"
          >
            <MdChevronRight size={16} />
          </button>
        )}

        {/* Dot Indicators */}
        {hasMultipleImages && (
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                  idx === currentImageIndex ? "bg-primary" : "bg-white/70"
                }`}
              />
            ))}
          </div>
        )}

        {/* Zoom Button */}
        <ZoomButton
          modalTarget="#modelZoomModal"
          onClick={(e) => {
            if (e) e.stopPropagation();
            const secondImg =
              images[(currentImageIndex + 1) % images.length] ||
              data.back ||
              "";
            if (openModal) {
              openModal(currentImg, secondImg);
            }
          }}
          positionClasses="absolute bottom-1.5 right-1.5"
          size="md"
        />
      </div>

      {/* Card Footer Info */}
      <div className="p-2 flex items-center justify-between gap-1 border-t border-slate-200/70 bg-white w-full min-w-0">
        <ExpandableTitle title={data.name || data.title} />
        {data.tier && (
          <span className="text-[8px] text-secondary bg-surface-container px-1 py-0.5 rounded font-semibold shrink-0">
            {data.tier}
          </span>
        )}
        {userRole === "admin" && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <Toggle
              checked={
                data.bIsActive === 1 ||
                data.bIsActive === true ||
                (data.bIsActive === undefined &&
                  (data.isActive === 1 ||
                    data.isActive === true ||
                    data.isActive === undefined))
              }
              onChange={() => handleToggleModel(data)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ModelSelectorDesktopView = ({
  searchTerm,
  onSearchChange,
  filteredData = [],
  openModal,
  setStep,
  step,
  setSelectedModel,
  selectedModel,
  selectedProduct,
  selectionMode = "user",
  onSelectionModeChange = null,
  onRefreshModels = null,
  isLoading = false,
  pagination = null,
  onPageChange = null,
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const { uploadModel, isLoading: isModelUploadLoading } = useModelUpload();
  const { role: userRole, token } = useSelector((state) => state.auth);
  const [, setToggleTick] = useState(0);
  const forceUpdate = () => setToggleTick((prev) => prev + 1);

  const handleUploadModalOpen = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadSubmit = async (dataPayload) => {
    try {
      const result = await uploadModel(dataPayload);

      if (result.success) {
        toast.success("Image uploaded successfully!");
        handleUploadModalClose();
        if (onRefreshModels) {
          onRefreshModels();
        }
      } else {
        toast.error(`Upload failed: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Upload error: ${error.message}`);
    }
  };

  const handleToggleModel = async (item) => {
    const itemId = item.id ?? item.iModelId ?? item.modelId;
    if (!itemId) return;
    const currentActiveVal = item.bIsActive ?? item.isActive;
    const previousState =
      currentActiveVal === 1 ||
      currentActiveVal === true ||
      currentActiveVal === undefined;
    const newState = !previousState;

    item.bIsActive = newState ? 1 : 0;
    item.isActive = newState ? 1 : 0;
    forceUpdate();

    try {
      const response = await apiHandler({
        url: `${endpoint.TOGGLE_MODEL}/${itemId}/toggle`,
        method: "PATCH",
        authToken: { token },
        noCache: true,
      });

      if (
        (response?.status && response.status >= 400) ||
        response?.data?.status === "error" ||
        response?.data?.success === false
      ) {
        throw new Error(
          response?.data?.message || "Failed to update model status",
        );
      }

      const resData = response?.data?.data || response?.data;
      if (resData) {
        const activeVal = resData.bIsActive ?? resData.isActive;
        if (activeVal !== undefined && activeVal !== null) {
          const finalActive = activeVal === 1 || activeVal === true ? 1 : 0;
          item.bIsActive = finalActive;
          item.isActive = finalActive;
        }
      }

      forceUpdate();
      toast.success(
        response?.data?.message ||
          (newState
            ? "Model enabled successfully"
            : "Model disabled successfully"),
      );
    } catch (err) {
      item.bIsActive = previousState ? 1 : 0;
      item.isActive = previousState ? 1 : 0;
      forceUpdate();
      toast.error(err?.message || "Failed to update model status");
    }
  };

  const stepTitle = "Select Model";
  const stepDesc = "Choose the model you want to try the garment on.";

  return (
    <>
      <div className="app-page flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-6">
          {/* Workspace Stepper */}
          <div className="px-6 pt-4 pb-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 shrink-0">
            <div>
              <h2 className="font-headline-md text-[20px] text-primary mb-0.5 font-bold tracking-tight">
                {stepTitle}
              </h2>
              <p className="font-body-md text-[10px] text-secondary">
                {stepDesc}
              </p>
            </div>

            <div className="app-glass-panel flex items-center px-4 py-2 self-start lg:self-auto">
              <div className="flex items-center gap-4 text-xs font-medium">
                <div
                  className={`flex items-center gap-2 ${step === 1 ? "text-primary" : "text-secondary"}`}
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      step === 1
                        ? "bg-primary text-on-primary ring-4 ring-primary/10"
                        : "border border-outline-variant bg-surface-container font-semibold"
                    }`}
                  >
                    1
                  </span>
                  <span className={step === 1 ? "font-semibold" : ""}>
                    Product
                  </span>
                </div>
                <div className="w-6 h-0.5 bg-outline-variant"></div>
                <div
                  className={`flex items-center gap-2 ${step === 2 ? "text-primary" : "text-secondary"}`}
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      step === 2
                        ? "bg-primary text-on-primary ring-4 ring-primary/10"
                        : "border border-outline bg-surface-container font-semibold"
                    }`}
                  >
                    2
                  </span>
                  <span className={step === 2 ? "font-semibold" : ""}>Model</span>
                </div>
                <div className="w-6 h-0.5 bg-outline-variant"></div>
                <div
                  className={`flex items-center gap-2 ${step === 3 ? "text-primary" : "text-secondary"}`}
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      step === 3
                        ? "bg-primary text-on-primary ring-4 ring-primary/10"
                        : "border border-outline bg-surface-container font-semibold"
                    }`}
                  >
                    3
                  </span>
                  <span className={step === 3 ? "font-semibold" : ""}>
                    Background
                  </span>
                </div>
                <div className="w-6 h-0.5 bg-outline-variant"></div>
                <div className="flex items-center gap-2 text-secondary">
                  <span className="w-5 h-5 rounded-full border border-outline-variant bg-surface-container flex items-center justify-center text-[10px] font-semibold">
                    4
                  </span>
                  <span>Review</span>
                </div>
              </div>
            </div>
          </div>

          {/* Selected Product Summary Banner */}
          <div className="px-6 pb-2 shrink-0">
            <section className="app-control-bar rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-14 h-14 rounded-md bg-surface-container border border-outline/70 overflow-hidden flex-shrink-0">
                  <img
                    className="w-full h-full object-contain mix-blend-multiply"
                    src={selectedProduct?.images?.[0] || ""}
                    alt="Selected garment"
                  />
                </div>
                <div className="min-w-0 flex flex-col justify-center">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[10px] text-secondary uppercase tracking-wider font-bold">
                      Selected Product
                    </p>
                    <span className="inline-flex px-1.5 py-0.5 rounded bg-surface-container border border-outline/70 font-semibold text-[8px] text-secondary shrink-0 uppercase tracking-wide">
                      {selectedProduct?.modelType === "mannequin"
                        ? "Mannequin"
                        : "Model"}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-primary truncate mt-0.5">
                    {selectedProduct?.title || "Selected Product"}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="app-button-secondary inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold focus:outline-none cursor-pointer"
              >
                <MdEdit className="text-sm" />
                <span>Change</span>
              </button>
            </section>
          </div>

          {/* Primary Controls (Sticky at top when scrolling) */}
          <div className="sticky top-0 z-30 px-6 py-2 bg-[#fff] ">
            <div className="app-control-bar rounded-lg p-2.5 sm:p-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex gap-0.5 bg-slate-100/80 p-0.5 rounded-md w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() =>
                    onSelectionModeChange && onSelectionModeChange("model")
                  }
                  className={`flex-1 sm:flex-none px-4 py-1.5 rounded font-semibold text-xs transition-all duration-200 focus:outline-none ${
                    selectionMode === "model"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  }`}
                >
                  Studio Models
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onSelectionModeChange && onSelectionModeChange("user")
                  }
                  className={`flex-1 sm:flex-none px-4 py-1.5 rounded font-semibold text-xs transition-all duration-200 focus:outline-none ${
                    selectionMode === "user"
                      ? "bg-primary text-on-primary shadow-sm font-bold border-none"
                      : "bg-white text-slate-500 border border-slate-200 hover:text-primary hover:border-slate-300 hover:shadow-sm"
                  }`}
                >
                  Users
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
                <SearchBar
                  value={searchTerm}
                  onChange={onSearchChange}
                  placeholder="Search models..."
                />

                {selectionMode === "user" && (
                  <button
                    type="button"
                    onClick={handleUploadModalOpen}
                    className="app-button-primary inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold focus:outline-none cursor-pointer"
                  >
                    <MdAdd className="text-sm" />
                    <span>Upload Image</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters & Grid Container */}
          <div className="px-6 pt-1">
            <section className="app-panel overflow-hidden">
              {isLoading ? (
                <div className="app-empty-state m-3 min-h-[260px] flex flex-col items-center justify-center text-center text-secondary">
                  <MdSync className="text-[36px] text-slate-300 animate-spin mb-2" />
                  <p className="text-xs font-semibold">
                    Loading models catalog...
                  </p>
                </div>
              ) : (!filteredData || filteredData.length === 0) ? (
                <div
                  id="empty-state"
                  className="app-empty-state m-3 min-h-[220px] flex flex-col items-center justify-center text-center text-secondary"
                >
                  <MdPersonSearch className="text-[36px] text-slate-300 mb-2" />
                  <p className="text-xs font-semibold">No models found</p>
                </div>
              ) : (
                <div
                  id="model-grid"
                  className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 p-3 animate-fade-in"
                >
                  {filteredData.map((data, idx) => {
                    const isActive = selectedModel?.name === data.name;

                    return (
                      <ModelCard
                        key={data.name || data.title || data.id || data.iModelId || idx}
                        data={data}
                        selectionMode={selectionMode}
                        isActive={isActive}
                        userRole={userRole}
                        onSelect={() => setSelectedModel(data)}
                        openModal={openModal}
                        handleToggleModel={handleToggleModel}
                      />
                    );
                  })}
                </div>
              )}
              {pagination && pagination.totalPages > 1 && (
                <div className="border-t border-slate-200/80 bg-white/86 px-3 py-1.5">
                  <InternalPagination
                    page={pagination.page}
                    limit={pagination.limit}
                    total={pagination.total}
                    totalPages={pagination.totalPages}
                    onPageChange={onPageChange}
                  />
                </div>
              )}
            </section>
          </div>
        </div>

        {/* Sticky Bottom Action Bar */}
        <footer
          id="action-footer"
          className="app-footer-bar w-full px-6 h-14 flex items-center z-10 shrink-0"
        >
          <div className="grid grid-cols-3 items-center w-full gap-3">
            <button
              onClick={() => setStep(step - 1)}
              className="justify-self-start flex items-center gap-2 text-secondary hover:text-primary transition-colors text-xs font-semibold focus:outline-none cursor-pointer"
            >
              <MdArrowBack className="text-[18px]" />
              <span>Back</span>
            </button>

            <div className="justify-self-center flex items-center gap-3 min-w-0 animate-fade-in">
              {selectedModel ? (
                <>
                  <div className="w-10 h-10 rounded-full border border-outline/60 overflow-hidden bg-surface-container shrink-0 flex items-center justify-center relative">
                    <img
                      className="w-full h-full object-cover object-top"
                      src={
                        selectedModel.front ||
                        selectedModel.images?.[0] ||
                        selectedModel.image
                      }
                      alt={selectedModel.name || selectedModel.title}
                    />
                  </div>
                  <div className="hidden sm:block min-w-0">
                    <span className="text-[10px] text-secondary block leading-tight font-semibold">
                      Selected
                    </span>
                    <span className="text-xs text-primary font-bold leading-tight truncate block">
                      {selectedModel.name || selectedModel.title}
                    </span>
                  </div>
                </>
              ) : (
                <span className="text-xs text-secondary font-semibold">
                  No selection
                </span>
              )}
            </div>

            <button
              onClick={() => setStep(step + 1)}
              disabled={!selectedModel}
              className={`app-button-primary justify-self-end px-6 py-2 rounded-md font-semibold text-xs focus:outline-none ${
                !selectedModel
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-primary/95 active:scale-[0.98] cursor-pointer"
              }`}
            >
              <span>Continue</span>
            </button>
          </div>
        </footer>
      </div>

      <UserImageUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadModalClose}
        onSubmit={handleUploadSubmit}
        isLoading={isModelUploadLoading}
        defaultGender={
          selectedProduct?.category === "men" ? "men" : "women"
        }
      />
    </>
  );
};

export default ModelSelectorDesktopView;
